import axios from 'axios';
import { io } from 'socket.io-client';
import { API_URL } from 'src/data/Api';
import { GLOB } from 'src/util/Glob';
import { WS_NS_RELOAD, WS_TOKEN_PARAM } from '@dto/websocketMessage.dto';
import { ClientDiagnostics, SELFTEST_LIMITS, SelftestEcho, SelftestTransport, SelftestUploadResult, TimingSample, TransportResult } from '@dto/selftest.dto';
import { errorMessage, gapsBetween, isBuffered, median, toResourceSample } from './connectionCheck.util';

export const LATENCY_SAMPLES = 5;
export const TRANSPORT_TIMEOUT_MS = 10_000;

const probeUrl = (path: string) => GLOB.SERVER_URL + API_URL.SELFTEST + path;

/** Steps reported through onProgress, in the order they run. */
export type ConnectionCheckStep = 'latency' | 'transports' | 'stream' | 'download' | 'upload' | 'resources';

async function timedEcho(): Promise<{ echo: SelftestEcho; sample: TimingSample; skewMs: number }> {
  const sentAt = Date.now();
  const started = performance.now();
  const { data: echo } = await axios.get<SelftestEcho>(API_URL.SELFTEST + '/echo');
  const clientMs = Math.round(performance.now() - started);
  return {
    echo,
    sample: { clientMs, serverMs: echo.processingMs },
    skewMs: Math.round(echo.receivedAt - (sentAt + clientMs / 2)),
  };
}

async function measureLatency(result: ClientDiagnostics) {
  const samples: TimingSample[] = [];
  try {
    for (let i = 0; i < LATENCY_SAMPLES; i++) {
      const { echo, sample, skewMs } = await timedEcho();
      samples.push(sample);
      result.echo = echo;
      result.clockSkewMs = skewMs;
    }
    const client = samples.map(s => s.clientMs);
    result.latency = { samples, medianMs: median(client), maxMs: Math.max(...client) };
  } catch (e) {
    result.latency = { samples, medianMs: NaN, maxMs: NaN, error: errorMessage(e) };
  }
}

/** One Socket.IO handshake per transport, no upgrade, so each is judged on its own. */
function tryTransport(transport: SelftestTransport): Promise<TransportResult> {
  return new Promise(resolve => {
    const started = performance.now();
    const finish = (res: Omit<TransportResult, 'ms'>) => {
      clearTimeout(guard);
      socket.close();
      resolve({ ...res, ms: Math.round(performance.now() - started) });
    };
    const socket = io(GLOB.SOCKET_IO.url + WS_NS_RELOAD, {
      addTrailingSlash: false,
      path: GLOB.SOCKET_IO.path,
      transports: [transport],
      upgrade: false,
      reconnection: false,
      timeout: TRANSPORT_TIMEOUT_MS,
      auth: { [WS_TOKEN_PARAM]: GLOB.userInfo?.wsToken },
      withCredentials: true,
    });
    socket.on('connect', () => finish({ connected: true }));
    socket.on('connect_error', err => finish({ connected: false, error: err.message }));
    // socket.io's own timeout surfaces as connect_error; this covers a handshake that never settles
    const guard = setTimeout(() => finish({ connected: false, error: 'timeout' }), TRANSPORT_TIMEOUT_MS * 2);
  });
}

async function measureTransports(result: ClientDiagnostics) {
  const [websocket, polling] = await Promise.all([tryTransport('websocket'), tryTransport('polling')]);
  result.transports = { websocket, polling };
}

/** Reads the chunked stream line by line and records when each line really arrived. */
async function measureStream(result: ClientDiagnostics) {
  const seconds = SELFTEST_LIMITS.STREAM_DEFAULT_SECONDS;
  const arrivals: number[] = [];
  try {
    const response = await fetch(probeUrl(`/stream?seconds=${seconds}`), { credentials: 'include' });
    if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let pending = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      pending += value;
      const lines = pending.split('\n');
      pending = lines.pop();
      for (const line of lines) {
        if (!line) continue;
        JSON.parse(line); // a malformed line means something rewrote the body
        arrivals.push(performance.now());
      }
    }
    const gapsMs = gapsBetween(arrivals);
    result.stream = { seconds, chunks: arrivals.length, gapsMs, buffered: isBuffered(gapsMs, SELFTEST_LIMITS.STREAM_CHUNK_INTERVAL_MS) };
  } catch (e) {
    const gapsMs = gapsBetween(arrivals);
    result.stream = { seconds, chunks: arrivals.length, gapsMs, buffered: false, error: errorMessage(e) };
  }
}

async function measureDownload(result: ClientDiagnostics) {
  const bytes = SELFTEST_LIMITS.BLOB_DEFAULT_BYTES;
  const started = performance.now();
  try {
    const response = await fetch(probeUrl(`/blob?bytes=${bytes}`), { credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = await response.arrayBuffer();
    result.download = { bytes: body.byteLength, ms: Math.round(performance.now() - started) };
  } catch (e) {
    result.download = { bytes: 0, ms: Math.round(performance.now() - started), error: errorMessage(e) };
  }
}

async function measureUpload(result: ClientDiagnostics) {
  const bytes = SELFTEST_LIMITS.BLOB_DEFAULT_BYTES;
  const started = performance.now();
  try {
    const { data } = await axios.post<SelftestUploadResult>(API_URL.SELFTEST + '/upload', new Uint8Array(bytes), {
      headers: { 'Content-Type': 'application/octet-stream' },
    });
    result.upload = { bytes: data.bytes, ms: Math.round(performance.now() - started) };
  } catch (e) {
    result.upload = { bytes: 0, ms: Math.round(performance.now() - started), error: errorMessage(e) };
  }
}

/**
 * Measures this browser's path to the backend. Never throws: each probe records its own error.
 * @param long also run the slow probes (streaming, download, upload), roughly a minute on a healthy link
 * @param onProgress called before each step
 */
export async function runConnectionCheck(long: boolean, onProgress?: (step: ConnectionCheckStep) => void): Promise<ClientDiagnostics> {
  const started = performance.now();
  const result: ClientDiagnostics = {
    startedAt: new Date().toISOString(),
    durationMs: 0,
    long,
    browser: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      location: window.location.href,
      cookiesEnabled: navigator.cookieEnabled,
      online: navigator.onLine,
    },
    resources: [],
  };

  const steps: [ConnectionCheckStep, (r: ClientDiagnostics) => Promise<void>][] = [
    ['latency', measureLatency],
    ['transports', measureTransports],
  ];
  if (long) steps.push(['stream', measureStream], ['download', measureDownload], ['upload', measureUpload]);

  for (const [step, run] of steps) {
    onProgress?.(step);
    await run(result);
  }

  onProgress?.('resources');
  result.resources = performance.getEntriesByType('resource').map(entry => toResourceSample(entry, window.location.origin));

  result.durationMs = Math.round(performance.now() - started);
  return result;
}
